import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import { getPoojaBookings } from '../../api/endpoints/pooja';

const statusColor = { pending: 'warning', confirmed: 'info', completed: 'success', cancelled: 'error' };

const PoojaOrderList = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getPoojaBookings(params);
      setBookings(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, debouncedSearch]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const columns = [
    { id: 'pujaName', label: 'Pooja', sortable: true, render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.pujaName || row.pujaType}</Typography>
        <Typography variant="caption" color="text.secondary">{row.pujaType}</Typography>
      </Box>
    )},
    { id: 'user', label: 'Customer', render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.userId?.name || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">{row.userId?.phone || ''}</Typography>
      </Box>
    )},
    { id: 'astrologer', label: 'Astrologer', render: (row) => (
      <Typography variant="body2">{row.astrologerId?.name || '-'}</Typography>
    )},
    { id: 'amount', label: 'Amount', align: 'right', render: (row) => (
      <Typography variant="body2" fontWeight={600}>{formatCurrency(row.amount)}</Typography>
    )},
    { id: 'date', label: 'Scheduled', render: (row) => (
      <Box>
        <Typography variant="caption" display="block">{row.date || '-'}</Typography>
        <Typography variant="caption" color="text.secondary">{row.time || ''}</Typography>
      </Box>
    )},
    { id: 'status', label: 'Status', render: (row) => (
      <Chip label={row.status} size="small" color={statusColor[row.status] || 'default'} />
    )},
    { id: 'createdAt', label: 'Booked On', render: (row) => formatDateTime(row.createdAt) },
    { id: 'actions', label: '', render: (row) => (
      <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/pooja-orders/${row._id}`)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Pooja Orders" subtitle="View and manage all pooja bookings" />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search by pooja name..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {Object.keys(statusColor).map((s) => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable columns={columns} rows={bookings} page={page} rowsPerPage={rowsPerPage} total={total}
          onPageChange={setPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} loading={loading}
          emptyMessage="No bookings yet" />
      )}
    </Box>
  );
};

export default PoojaOrderList;
